import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Button, Grid, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import PaperContainer from '@fd/ui/Layout/PaperContainer';

import { FIVE_MINUTES } from '../../../../../../../helpers/timeConstants';
import { formikValidate } from '../../../../../../../helpers/validation';
import { notifyError, NotifyProps, notifySaved } from '../../../../../../../layouts/Notify/actions';
import { WhiteLabelAppStoreConfig, whitelabelService } from '../../../../whitelabel.service';

type Props = MappedState & MappedDispatch;

export type AppStoreFormValues = {
  ReadyToPublishToAppStore: boolean;
  AppStoreName: string;
  appStoreAppleId: string;
  AppStoreUrl: string;
  AppStoreId: string;
  AppStorePassword: string;
  TeamId: string;
  AppGroupId: string;
  BundleId: string;
  AppStoreSigningIdentity: string;
  AppStoreToken: string;
  AppStoreMerchantId: string;
  P8PrivateKey: string;
  P8PrivateKeyId: string;
};

const AppStoreConfigurationTab = ({
  translate,
  dispatchNotifyError,
  dispatchNotifySaved,
}: Props) => {
  const currentApp = useAppSelector((state) => state.currentApp);
  const queryClient = useQueryClient();
  const appId = currentApp?.AppId || '';

  // Query for App Store configuration
  const {
    data: appStoreConfig,
    isLoading,
    error: configError,
  } = useQuery({
    queryKey: ['appStoreConfig', appId],
    queryFn: () => whitelabelService.getWhitelabelAppStoreConfig(appId),
    enabled: !!appId,
    staleTime: FIVE_MINUTES,
  });

  // Mutation for updating App Store configuration
  const { mutate: updateAppStoreConfig, isPending: isUpdating } = useMutation({
    mutationFn: (config: WhiteLabelAppStoreConfig) =>
      whitelabelService.updateWhiteLabelAppStoreConfig(appId, config),
    onSuccess: () => {
      dispatchNotifySaved();
      queryClient.invalidateQueries({
        queryKey: ['appStoreConfig', appId],
      });
    },
    onError: (error: any) => {
      dispatchNotifyError({
        message: error.message || 'Failed to save App Store configuration',
        translate: true,
      });
    },
  });
  // Create initial values from fetched data or defaults
  const initialValues = useMemo(() => {
    return {
      ReadyToPublishToAppStore: appStoreConfig?.ReadyToPublishToAppStore || false,
      AppStoreName: appStoreConfig?.AppStoreName || '',
      appStoreAppleId: appStoreConfig?.AppStoreAppleId || 'developers@flipdish.com',
      AppStoreUrl: appStoreConfig?.AppStoreUrl || '',
      AppStoreId: appStoreConfig?.AppStoreId || '',
      AppStorePassword: appStoreConfig?.AppStorePassword || '',
      TeamId: appStoreConfig?.TeamId || '',
      AppGroupId: appStoreConfig?.AppGroupId || '',
      BundleId: appStoreConfig?.BundleId || '',
      AppStoreSigningIdentity: appStoreConfig?.AppStoreSigningIdentity || '',
      AppStoreToken: appStoreConfig?.AppStoreToken || '',
      AppStoreMerchantId: appStoreConfig?.AppStoreMerchantId || '',
      P8PrivateKey: appStoreConfig?.P8PrivateKey || '',
      P8PrivateKeyId: appStoreConfig?.P8PrivateKeyId || '',
    };
  }, [appStoreConfig]);

  const validateAppStoreUrl = (value: string) => {
    if (value && !value.startsWith('https://')) {
      return translate('App_store_url_must_start_with_itunes') as string;
    }
    return null;
  };

  const validateAppGroupId = (value: string) => {
    if (value && !value.startsWith('group.')) {
      return translate('App_group_id_must_start_with_group') as string;
    }
    return null;
  };

  const validateMerchantId = (value: string) => {
    if (value && !value.startsWith('merchant.')) {
      return translate('Merchant_id_must_start_with_merchant') as string;
    }
    return null;
  };

  const handleSubmit = useCallback(
    async (values: AppStoreFormValues) => {
      updateAppStoreConfig(values);
    },
    [updateAppStoreConfig]
  );

  if (isLoading) {
    return (
      <PaperContainer fluid>
        <Grid container spacing={3} sx={{ padding: '24px' }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <Translate id="App_Store_Configuration" />
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Loading App Store configuration...
            </Typography>
          </Grid>
        </Grid>
      </PaperContainer>
    );
  }

  return (
    <PaperContainer fluid>
      <Grid container spacing={3} sx={{ padding: '24px' }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            <Translate id="App_Store_Configuration" />
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <Translate id="Configure_app_store_settings_and_deployment" />
          </Typography>
        </Grid>

        {/* Config Error Message */}
        {configError && (
          <Grid item xs={12}>
            <Typography variant="body2" color="error">
              Failed to load App Store configuration. Please try again.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            validate={(values) => {
              const errors: Partial<AppStoreFormValues> = {};

              // Validate app store name length
              if (values.AppStoreName && values.AppStoreName.length > 80) {
                errors.AppStoreName = translate('App_store_name_max_80_characters') as string;
              }

              // Validate email format for Apple ID
              if (values.appStoreAppleId && formikValidate.email(values.appStoreAppleId)) {
                errors.appStoreAppleId = formikValidate.email(values.appStoreAppleId);
              }

              // Validate app store URL format
              const urlError = validateAppStoreUrl(values.AppStoreUrl);
              if (urlError) {
                errors.AppStoreUrl = urlError;
              }

              // Validate app group ID format
              const appGroupError = validateAppGroupId(values.AppGroupId);
              if (appGroupError) {
                errors.AppGroupId = appGroupError;
              }

              // Validate merchant ID format
              const merchantError = validateMerchantId(values.AppStoreMerchantId);
              if (merchantError) {
                errors.AppStoreMerchantId = merchantError;
              }

              return Object.keys(errors).length > 0 ? errors : undefined;
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
              <Form>
                {errors && (
                  <Grid container spacing={3} sx={{ marginBottom: 2 }}>
                    {Object.entries(errors).map(([key, value]) => (
                      <Grid key={key} item xs={12} sx={{ marginBottom: 1 }}>
                        <Typography variant="body2" color="error">
                          {typeof value === 'string'
                            ? value
                            : value?.toString?.() || `Error at ${key}`}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Grid container spacing={3}>
                  {/* App Store Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreName"
                      label={translate('App_store_name')}
                      value={values.AppStoreName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreName && errors.AppStoreName)}
                      fullWidth
                      inputProps={{ maxLength: 80 }}
                    />
                  </Grid>

                  {/* App Store Apple ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="appStoreAppleId"
                      label={translate('App_Store_AppleId')}
                      type="email"
                      value={values.appStoreAppleId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.appStoreAppleId && errors.appStoreAppleId)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Store URL */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreUrl"
                      label={translate('App_Store_Url')}
                      value={values.AppStoreUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreUrl && errors.AppStoreUrl)}
                      fullWidth
                      placeholder="https://itunes.apple.com/app/"
                    />
                  </Grid>

                  {/* App Store ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreId"
                      label={translate('App_Store_Id')}
                      value={values.AppStoreId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreId && errors.AppStoreId)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Store Password */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStorePassword"
                      label={translate('App_Store_Password')}
                      type="password"
                      value={values.AppStorePassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStorePassword && errors.AppStorePassword)}
                      fullWidth
                    />
                  </Grid>

                  {/* Team ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="TeamId"
                      label={translate('Team_Id')}
                      value={values.TeamId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.TeamId && errors.TeamId)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Group ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppGroupId"
                      label={translate('App_Group_Id')}
                      value={values.AppGroupId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppGroupId && errors.AppGroupId)}
                      fullWidth
                      placeholder="group.<bundleid>"
                    />
                  </Grid>

                  {/* Bundle ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="BundleId"
                      label={translate('Bundle_Id')}
                      value={values.BundleId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.BundleId && errors.BundleId)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Store Signing Identity */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreSigningIdentity"
                      label={translate('App_Store_Signing_Identity')}
                      value={values.AppStoreSigningIdentity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreSigningIdentity && errors.AppStoreSigningIdentity)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Store Token */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreToken"
                      label={translate('App_Store_Token')}
                      type="password"
                      value={values.AppStoreToken}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreToken && errors.AppStoreToken)}
                      fullWidth
                    />
                  </Grid>

                  {/* App Store Merchant ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="AppStoreMerchantId"
                      label={translate('App_Store_Merchant_Id')}
                      value={values.AppStoreMerchantId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.AppStoreMerchantId && errors.AppStoreMerchantId)}
                      fullWidth
                      placeholder="merchant.<bundleid>"
                    />
                  </Grid>

                  {/* P8 Private Key */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="P8PrivateKey"
                      label={translate('P8_Private_Key')}
                      type="password"
                      value={values.P8PrivateKey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.P8PrivateKey && errors.P8PrivateKey)}
                      fullWidth
                    />
                  </Grid>

                  {/* P8 Private Key ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="P8PrivateKeyId"
                      label={translate('P8_Private_Key_Id')}
                      value={values.P8PrivateKeyId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.P8PrivateKeyId && errors.P8PrivateKeyId)}
                      fullWidth
                    />
                  </Grid>

                  {/* Ready to Publish Switch */}
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Switch
                          name="ReadyToPublishToAppStore"
                          checked={values.ReadyToPublishToAppStore}
                          onChange={handleChange}
                          color="primary"
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">
                          <Translate id="Ready_to_publish_to_app_store" />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isUpdating || !dirty}
                      sx={{ mt: 2 }}
                    >
                      {isUpdating ? (
                        <Translate id="Saving" />
                      ) : (
                        <Translate id="Save_Configuration" />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </PaperContainer>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state),
});

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaved: () => dispatch(notifySaved()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppStoreConfigurationTab);
